import { useMemo } from "react";

import { EntitySchemaResource, useEntitySchemas } from "src/entities/queries";
import { useFeatures } from "src/featureCatalogue/queries";
import { useWorkspaceContext } from "src/router/routerContextHooks";
import { EntitySchemaHint } from "src/schema/EntitySchemaHint";
import {
  decodeJsonRefPath,
  isJsonRefPath,
  isReferencingEntity,
  JsonRefPath,
} from "src/schema/jsonRefUtils";
import { SchemaGlobalFeatures } from "src/schema/SchemaGlobalFeatures";
import { getEntitySchemaOptions } from "src/schema/SchemaTypeSelector";
import { FieldAccessInfo } from "src/schema/schemaEditor/FieldAccessInfo";
import { SchemaTypeSelector } from "src/schema/schemaEditor/SchemaTypeSelector";
import { SchemaEditorProps } from "src/schema/schemaEditor/types";
import { getFeaturesByEntity } from "src/schema/schemaMappingUtils";

const useSchemaData = () => {
  const { workspace } = useWorkspaceContext();
  const { data: entitySchemasData } = useEntitySchemas({
    baseUrl: workspace.base_url!,
  });
  const { data: features } = useFeatures();

  const schemasOptions = getEntitySchemaOptions(
    entitySchemasData?.entities ?? [],
  );

  const featuresByEntity = useMemo(
    () => getFeaturesByEntity(features ?? []),
    [features],
  );

  return {
    entitySchemasData,
    schemasOptions,
    featuresByEntity,
  };
};

const useSelectedEntity = (
  formMethods: SchemaEditorProps["formMethods"],
  entitySchemasData: { entities: EntitySchemaResource[] } | undefined,
) => {
  const properties = formMethods.getValues("properties") ?? [];
  const entityProperty = properties.find(
    (property) =>
      property.type &&
      isJsonRefPath(property.type[0]) &&
      isReferencingEntity(property.type[0] as JsonRefPath),
  );

  const selectedEntitySchema = entityProperty?.type?.[0];
  const hasNoSchemas = entitySchemasData?.entities.length === 0;

  const decodedRef = decodeJsonRefPath(selectedEntitySchema ?? "");
  const selectedEntity =
    decodedRef?.metaType === "entity"
      ? entitySchemasData?.entities.find(
          (entity: EntitySchemaResource) => entity._id === decodedRef.type,
        )
      : undefined;

  return {
    entityProperty,
    selectedEntitySchema,
    hasNoSchemas,
    selectedEntity,
  };
};

export const EntitySchemaEditor: React.FC<SchemaEditorProps> = ({
  formMethods,
  isReadonly,
}) => {
  const { entitySchemasData, schemasOptions, featuresByEntity } =
    useSchemaData();
  const { entityProperty, selectedEntitySchema, hasNoSchemas, selectedEntity } =
    useSelectedEntity(formMethods, entitySchemasData);

  return (
    <>
      <SchemaTypeSelector
        formMethods={formMethods}
        hasNoSchemas={hasNoSchemas}
        isReadonly={isReadonly}
        schemasOptions={schemasOptions}
      />
      {selectedEntitySchema && (
        <FieldAccessInfo
          inputType="entity"
          schemaName={entityProperty?.fieldName ?? ""}
        />
      )}
      {entitySchemasData && selectedEntity && (
        <EntitySchemaHint
          entitySchema={selectedEntity}
          entitySchemas={entitySchemasData.entities}
          featuresByEntity={featuresByEntity}
          rootPath={`data.${entityProperty?.fieldName}`}
        />
      )}
      <SchemaGlobalFeatures />
    </>
  );
};
